/**
 * GUSTHÓ — global metal preference (silver | gold).
 *
 * Persists in localStorage. Bidirectionally synced with the
 * `filter.v.t.shopify.color-pattern` URL parameter on /collections/* views
 * (the single PLP color axis; was gustho.metal_color):
 *   - On collection load: if preference is set, the URL has no color filter
 *     AND the collection actually offers the preferred value, redirect with
 *     the filter applied. The available values come from
 *     snippets/metal-color-axis-values.liquid — without that guard the
 *     preference also hits collections that don't run on the metal axis
 *     (leather belts), filtering every product away.
 *   - When the URL changes (chip removal, history navigation): if the
 *     color filter is cleared, the preference is cleared too.
 *   - Clicking a .metal-pair-swatches__dot anywhere stores the metal as
 *     the new preference. On a collection page the click is intercepted
 *     and turned into a filter-apply redirect; on PDP/home the default
 *     <a> navigation continues so the user lands on the sibling product.
 */
(function () {
  const KEY = 'gustho:metal_preference';
  // Single PLP color axis = Shopify taxonomy attribute shopify.color-pattern,
  // exposed as a VARIANT-taxonomy storefront filter (filter.v.t.…). The product
  // metafield gustho.metal_color still exists (read by the card swatches +
  // dedup); only the storefront FILTER moved to color-pattern.
  const FILTER_PARAM = 'filter.v.t.shopify.color-pattern';
  // Logical metal -> storefront filter value token. Shopify uses the global
  // taxonomy-value GID here (verified live): Gold = …/4, Silber = …/5.
  const TOKENS = {
    silver: 'gid://shopify/TaxonomyValue/5',
    gold: 'gid://shopify/TaxonomyValue/4',
  };
  const VALID = ['silver', 'gold'];

  // URL filter token -> logical metal. Tolerates the handle, a localized label
  // (Silber), or any future token containing the color word.
  const metalFromToken = (tok) => {
    if (tok === TOKENS.gold) return 'gold';
    if (tok === TOKENS.silver) return 'silver';
    const t = (tok || '').toLowerCase();
    if (t.indexOf('gold') !== -1) return 'gold';
    if (t.indexOf('silver') !== -1 || t.indexOf('silber') !== -1) return 'silver';
    return null;
  };

  const getPref = () => {
    const v = localStorage.getItem(KEY);
    return VALID.includes(v) ? v : null;
  };
  const setPref = (metal) => {
    if (VALID.includes(metal)) localStorage.setItem(KEY, metal);
  };
  const clearPref = () => localStorage.removeItem(KEY);

  const isCollectionView = () =>
    window.location.pathname.startsWith('/collections/');

  // Color-axis values the current collection actually offers, emitted by
  // snippets/metal-color-axis-values.liquid. Not every collection runs on the
  // metal axis — leather belts carry their own color-pattern values (Nero,
  // Testa di Moro), so applying a silver/gold preference there filters every
  // product away.
  //
  // Missing data counts as "no axis" on purpose: a preference that silently
  // stops applying is a far smaller failure than a collection that shows
  // nothing at all.
  const collectionHasMetal = (metal) => {
    const values = window.GUSTHO_COLOR_AXIS_VALUES;
    if (!Array.isArray(values)) return false;
    return values.indexOf(TOKENS[metal]) !== -1;
  };

  const filterValuesFromURL = (href) => {
    const u = new URL(href, window.location.origin);
    return u.searchParams.getAll(FILTER_PARAM);
  };

  const urlWithFilter = (href, metal) => {
    const u = new URL(href, window.location.origin);
    u.searchParams.delete(FILTER_PARAM);
    u.searchParams.append(FILTER_PARAM, TOKENS[metal] || metal);
    return u.toString();
  };

  // Apply the stored preference on load if not already filtered.
  // Runs before any other init so the redirect happens with minimal flash.
  const autoApply = () => {
    if (!isCollectionView()) return;
    const pref = getPref();
    if (!pref) return;
    const present = filterValuesFromURL(window.location.href);
    if (present.length > 0) return;
    if (!collectionHasMetal(pref)) return;
    window.location.replace(urlWithFilter(window.location.href, pref));
  };

  // Sync localStorage from the current URL (initial + on history changes).
  const syncFromURL = () => {
    if (!isCollectionView()) return;
    const present = filterValuesFromURL(window.location.href);
    if (present.length === 1) {
      const metal = metalFromToken(present[0]);
      if (metal) setPref(metal);
    } else if (present.length === 0) {
      // User explicitly cleared the color filter (chip removal etc.)
      clearPref();
    }
    // Multi-select (both silver+gold) → leave preference untouched.
  };

  // Patch History API so AJAX-driven URL changes (Shopify facets.js) trigger
  // syncFromURL — chip removal updates URL via pushState/replaceState.
  const watchURL = () => {
    window.addEventListener('popstate', syncFromURL);
    ['pushState', 'replaceState'].forEach((m) => {
      const orig = history[m];
      history[m] = function (...args) {
        const result = orig.apply(this, args);
        syncFromURL();
        return result;
      };
    });
  };

  const metalFromDot = (dot) => {
    if (dot.classList.contains('metal-pair-swatches__dot--silver'))
      return 'silver';
    if (dot.classList.contains('metal-pair-swatches__dot--gold'))
      return 'gold';
    return null;
  };

  const onSwatchClick = (e) => {
    const dot = e.target.closest('.metal-pair-swatches__dot');
    if (!dot) return;
    const metal = metalFromDot(dot);
    if (!metal) return;

    setPref(metal);

    if (isCollectionView() && collectionHasMetal(metal)) {
      e.preventDefault();
      const next = urlWithFilter(window.location.href, metal);
      if (next !== window.location.href) window.location.assign(next);
    }
    // Other views — and collections that don't run on the metal axis: the
    // default <a> navigation continues so the user lands on the sibling
    // product; the preference is set for the next axis-carrying collection.
  };

  // Init order: autoApply must run before anything else can paint.
  autoApply();
  // Initial URL → preference sync (e.g. shared link with ?filter=...)
  if (!filterValuesFromURL(window.location.href).length) {
    // No filter in URL — keep existing preference (autoApply handled it
    // already if applicable).
  } else {
    syncFromURL();
  }
  watchURL();
  document.addEventListener('click', onSwatchClick, { capture: true });
})();
